import { useEffect, useState } from "react";
import { MdAdd, MdEdit, MdDelete, MdLockReset } from "react-icons/md";
import Card from "components/card";
import { Psychologist, PsychologistResponse } from "types/psychologist";
import ProfessionalForm from "./ProfessionalForm";
import { psychologistService } from "services/psychologists";

const Professionals = () => {
  const [psychologists, setPsychologists] = useState<Psychologist[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedPsychologist, setSelectedPsychologist] = useState<Psychologist | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<number | null>(null);

  const fetchPsychologists = async () => {
    try {
      setLoading(true);
      const response = await psychologistService.getAll();
      if (response.success) {
        setPsychologists(response.data);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError("Failed to fetch psychologists");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPsychologists();
  }, []);

  const handleAddSuccess = () => {
    setIsFormOpen(false);
    setSelectedPsychologist(null);
    fetchPsychologists();
  };

  const handleEdit = (psychologist: Psychologist) => {
    setSelectedPsychologist(psychologist);
    setIsFormOpen(true);
  };

  const handlePasswordReset = (psychologistId: number) => {
    // TODO: Implement password reset functionality
    console.log('Password reset for psychologist:', psychologistId);
  };

  const handleToggleActive = async (psychologist: Psychologist) => {
    try {
      setUpdatingStatus(psychologist.id);
      const response = await psychologistService.toggleActive(psychologist.id);

      if (response.success) {
        // Update the local state
        setPsychologists(prevPsychologists =>
          prevPsychologists.map(p =>
            p.id === psychologist.id
              ? {
                  ...p,
                  psychologist_profile: {
                    ...p.psychologist_profile,
                    is_active: !p.psychologist_profile.is_active
                  }
                }
              : p
          )
        );
      } else {
        setError(response.message || "Failed to update status");
      }
    } catch (err) {
      setError("Failed to update psychologist status");
      console.error(err);
    } finally {
      setUpdatingStatus(null);
    }
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-lg font-semibold text-navy-700 dark:text-white">
          Loading...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-lg font-semibold text-red-500">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 grid h-full">
      <div className="col-span-12 h-fit w-full xl:col-span-12 2xl:col-span-12">
        <Card extra={"w-full h-full px-6 pb-6 sm:overflow-x-auto"}>
          <div className="relative flex items-center justify-between pt-4">
            <div className="text-xl font-bold text-navy-700 dark:text-white">
              Profesionales
            </div>
            <button 
              onClick={() => setIsFormOpen(true)}
              className="flex items-center justify-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-white hover:bg-brand-600"
            >
              <MdAdd className="h-6 w-6" />
              <span>Agregar Profesional</span>
            </button>
          </div>
          
          <div className="mt-8">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="min-w-[150px] py-3 pr-4 text-left">
                    <p className="text-sm font-bold text-gray-600">Nombre</p>
                  </th>
                  <th className="min-w-[150px] py-3 pr-4 text-left">
                    <p className="text-sm font-bold text-gray-600">Email</p>
                  </th>
                  <th className="min-w-[150px] py-3 pr-4 text-left">
                    <p className="text-sm font-bold text-gray-600">Teléfono</p>
                  </th>
                  <th className="min-w-[150px] py-3 pr-4 text-left">
                    <p className="text-sm font-bold text-gray-600">Licencia</p>
                  </th>
                  <th className="min-w-[200px] py-3 pr-4 text-left">
                    <p className="text-sm font-bold text-gray-600">Dirección</p>
                  </th>
                  <th className="min-w-[100px] py-3 pr-4 text-left">
                    <p className="text-sm font-bold text-gray-600">Estado</p>
                  </th>
                  <th className="py-3 pr-4 text-left">
                    <p className="text-sm font-bold text-gray-600">Acciones</p>
                  </th>
                </tr>
              </thead>
              <tbody>
                {psychologists.map((psychologist) => (
                  <tr key={psychologist.id} className="border-b border-gray-200">
                    <td className="py-3 pr-4">
                      <p className="text-sm font-medium text-navy-700 dark:text-white">
                        {psychologist.name}
                      </p>
                    </td>
                    <td className="py-3 pr-4">
                      <p className="text-sm font-medium text-gray-600">
                        {psychologist.email}
                      </p>
                    </td>
                    <td className="py-3 pr-4">
                      <p className="text-sm font-medium text-gray-600">
                        {psychologist.psychologist_profile.phone}
                      </p>
                    </td>
                    <td className="py-3 pr-4">
                      <p className="text-sm font-medium text-gray-600">
                        {psychologist.psychologist_profile.license_id}
                      </p>
                    </td>
                    <td className="py-3 pr-4">
                      <p className="text-sm font-medium text-gray-600">
                        {psychologist.psychologist_profile.address}
                      </p>
                    </td>
                    <td className="py-3 pr-4">
                      <div className="flex items-center">
                        <button
                          onClick={() => handleToggleActive(psychologist)}
                          disabled={updatingStatus === psychologist.id}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 ${
                            psychologist.psychologist_profile.is_active
                              ? 'bg-brand-500'
                              : 'bg-gray-200'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              psychologist.psychologist_profile.is_active ? 'translate-x-6' : 'translate-x-1'
                            } ${updatingStatus === psychologist.id ? 'opacity-50' : ''}`}
                          />
                        </button>
                        <span className="ml-2 text-sm font-medium text-gray-600">
                          {psychologist.psychologist_profile.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-2">
                        <button
                          className="rounded-lg p-2 text-brand-500 hover:bg-gray-100"
                          title="Editar"
                          onClick={() => handleEdit(psychologist)}
                        >
                          <MdEdit className="h-5 w-5" />
                        </button>
                        <button
                          className="rounded-lg p-2 text-yellow-500 hover:bg-gray-100"
                          title="Resetear Contraseña"
                          onClick={() => handlePasswordReset(psychologist.id)}
                        >
                          <MdLockReset className="h-5 w-5" />
                        </button>
                        <button
                          className="rounded-lg p-2 text-red-500 hover:bg-gray-100"
                          title="Eliminar"
                        >
                          <MdDelete className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {isFormOpen && (
        <ProfessionalForm
          onClose={() => {
            setIsFormOpen(false);
            setSelectedPsychologist(null);
          }}
          onSuccess={handleAddSuccess}
          professional={selectedPsychologist}
        />
      )}
    </div>
  );
};

export default Professionals; 